import { useTranslation } from "react-i18next";
import { Space, type TableColumnsType } from "antd";

import { Table } from "@av/components/atoms/table/Table";
import { UserAvatar } from "@av/components/molecules/EmployeeAvatar";
import { getConsistentColorForString } from "@av/components/utilities/color-utilities";
import { getInitials } from "@av/utils/domain/user/initials-utilities";
import { headerCell, type HrAdminSettingsUser } from "../../../../../services/admin-settings/type";
import { compareText } from "../../../../hooks/filter-admin-settings-users";
import { createEmailColumn } from "../../common/user-table/EmailColumn";
import { createSecurityGroupColumn } from "../../common/user-table/SecurityGroupColumn";

type SecurityGroupUsersTableProps = {
  // The caller must supply company-scoped, nonterminated members of the selected group.
  users: HrAdminSettingsUser[];
  isLoading?: boolean;
};

export function SecurityGroupUsersTable({
  users,
  isLoading = false,
}: SecurityGroupUsersTableProps) {
  const { t } = useTranslation();
  const testIdPrefix = "security-group-users";
  const columns: TableColumnsType<HrAdminSettingsUser> = [
    {
      title: t("platform.adminSettings.users_columns_employeeNumber"),
      dataIndex: "employeeNumber",
      key: "employeeNumber",
      defaultSortOrder: "ascend",
      sorter: (a, b) => compareText(a.employeeNumber, b.employeeNumber),
      onHeaderCell: headerCell(`${testIdPrefix}-column-employee-number`),
      width: 150,
    },
    {
      title: t("platform.adminSettings.users_columns_name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => compareText(a.name, b.name),
      onHeaderCell: headerCell(`${testIdPrefix}-column-name`),
      width: 280,
      render: (name: string, user) => (
        <Space>
          <UserAvatar
            testIdPrefix={`${testIdPrefix}-avatar-${user.employeeNumber}`}
            info={{
              avatarUri: user.avatarUri,
              avatarColor: getConsistentColorForString(user.name),
              initials: getInitials(user.name),
            }}
            isDemo={false}
          />
          {name}
        </Space>
      ),
    },
    createSecurityGroupColumn<HrAdminSettingsUser>({
      t,
      productCode: "hr",
      testIdPrefix,
      getTestIdValue: (user) => user.employeeNumber,
    }),
    createEmailColumn<HrAdminSettingsUser>({
      t,
      testIdPrefix,
      getTestIdValue: (user) => user.employeeNumber,
    }),
    {
      title: t("platform.adminSettings.users_columns_jobTitle"),
      dataIndex: "jobTitle",
      key: "jobTitle",
      onHeaderCell: headerCell(`${testIdPrefix}-column-job-title`),
      width: 240,
    },
  ];

  return (
    <Table<HrAdminSettingsUser>
      columns={columns}
      dataSource={users}
      loading={isLoading}
      pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 25, 50, 100] }}
      rowKey="userId"
      sortDirections={["ascend", "descend", "ascend"]}
      testIdPrefix={testIdPrefix}
    />
  );
}
